import { getConfigData } from "../../../services/configurationService";

export interface ColumnMetaData {
  headerName: string;
  mappedValue: string;
  width: number;
  headerAlign: string;
  align: string;
  type?: string; 
  headerParts?:any
  hasEditPenSvg?:boolean;
  hasInfoTooltip?:boolean;
  infoTooltipMessage?: string;
  includeCurrenySymbol?:boolean;
}

export interface CreateClaimColumnsMetaData {
  columns: ColumnMetaData[];
  Actions: string[];
}

export const getSaleReturnsColumnsConfig = async (): Promise<any[] | null> => {
  try {
    const config = await getConfigData("dms_configuration","claimsSaleReturnsColumns");
    return config[0]?.configValue || null;
  } catch (error) {
    console.error("Error fetching column configuration:", error);
    return null;
  }
};

export const getDefaultSaleReturnsColumns = (): any[] => [
  {
    id: "skuCode",
    headerName: "SKU Code",
    mappedValue: "skuCode",
    width: 160,
    headerAlign: "left",
    align: "left",
    hasInfoTooltip: true,
    infoTooltipMessage: "SKUs marked with red warning icon are non-eligible.",
    enabled: true,
    order: 0,
  },
  {
    id: "skuName",
    headerName: "SKU Name",
    mappedValue: "skuName",
    width: 270,
    headerAlign: "left",
    align: "left",
    enabled: true,
    order: 1,
  },
  {
    id: "returnOrderNo",
    headerName: "Return Order No.",
    mappedValue: "returnOrderNo",
    width: 180,
    headerAlign: "left",
    align: "left",
    enabled: true,
    order: 2,
  },
  {
    id: "damageQty",
    headerName: "Damage Qty",
    mappedValue: "cases,pieces,others",
    width: 160,
    headerAlign: "left",
    align: "left",
    type: "order",
    hasEditPenSvg: true,
    enabled: true,
    order: 3,
  },
  {
    id: "totalCreditGiven",
    headerName: "Total Credit Given",
    headerParts: { top: "Total Credit", bottom: "Given" },
    mappedValue: "totalCreditGiven",
    width: 160,
    headerAlign: "left",
    align: "left",
    hasInfoTooltip: true,
    infoTooltipMessage: "Returns with no credit note can not be claimed.",
    enabled: true,
    order: 4,
  },
  {
    id: "totalClaimAmount",
    headerName: "Total Claim Amount",
    headerParts: { top: "Total Claim", bottom: "Amount" },
    mappedValue: "totalClaimAmount",
    width: 160,
    headerAlign: "left",
    align: "left",
    includeCurrenySymbol: true,
    hasInfoTooltip: true,
    infoTooltipMessage: "SKUs with 0 claim amount cannot be raised for claim.",
    enabled: true,
    order: 5,
  },
];

export const getSaleReturnsColumnsMetaData = async (
  isNonEligible: boolean,
  allowZeroCreditNoteClaims: boolean
) => {
  let columnsConfig = await getSaleReturnsColumnsConfig();
  if (!columnsConfig || columnsConfig.length === 0) {
    columnsConfig = getDefaultSaleReturnsColumns();
  }

  const enabledColumns = columnsConfig
    .filter((column) => column.enabled)
    .sort((a, b) => a.order - b.order)
    .map((column) => {
      const baseColumn = { ...column };

      if (baseColumn.id === "skuCode") {
        return {
          ...baseColumn,
          hasInfoTooltip: isNonEligible,
        };
      }
      if (baseColumn.id === "totalCreditGiven") {
        return {
          ...baseColumn,
          hasInfoTooltip: !allowZeroCreditNoteClaims,
        };
      }
      return baseColumn;
    });

  return {
    columns: enabledColumns,
    Actions: ["select"],
  };
};
export interface SaleReturnRow {
  id: string;
  skuCode: string;
  skuName: string;
  returnOrderNo: Record<string, any>;
  invoices?: any[];
  cases: number;
  pieces: number;
  others: number;
  totalCreditGiven: string | number;
  totalClaimAmount: number;
  ptdCs: number;
  ptdPc: number;
  ptdOther: number;
  initialCases?: number;  
  initialPieces?: number;
  initialOthers?: number; 
}
export interface Row {
  id: number;
  skuCode: string;
  skuName: string;
  returnOrderNo: string;
  cases: number;
  pieces: number;
  others: number;
  totalCreditGiven: number;
  reason: string;
  totalClaimAmount: number;
}

export interface ReasonDetail {
  reason: string;
  caseQty: number;
  otherQty: number;
  pieceQty: number;
}

export interface BatchNumberObj {
  id: string;
  stockType?: string;
  batchId: string;
  reasonDetails: ReasonDetail[];
  ptdCs?: number;
  ptdPc?: number;
  ptdOther?: number;
}

export interface Batch {
  batchId: string;
  claimableCs: number;
  claimablePc: number;
  claimableOther: number;
  returnReasonArray: any[];
  approvals?: any[];
}

export type QuantityType = 'caseQty' | 'pieceQty' | 'otherQty';

export interface BatchEditorProps {
  batch: BatchNumberObj;
  originalBatch: BatchNumberObj | undefined;
  onChange: (index: number, key: keyof ReasonDetail, value: any) => void;
  onReset: (index: number) => void;
  onSave: () => void;
  caseUnitLabel: string;
  otherUnitLabel: string;
  packUnitLabel: string;
}
export interface InvoiceData {
  batchIds: Batch[];
}

export interface BatchContainerProps {
  batch: BatchNumberObj;
  expandedBatchIds: string[];
  setExpandexBatchIds: React.Dispatch<React.SetStateAction<string[]>>;
  filteredStaticBatches: BatchNumberObj[];
  setFilteredStaticBatches: React.Dispatch<
    React.SetStateAction<BatchNumberObj[]>
  >;
  staticBatches: any[];
  disableEditing:any
}

export interface totalCsPc {
  totalCs: number;
  totalPc: number;
}
export interface LocationState {
  switchState: number;
  month: string;
}

export const getPurchaseReturnsColumnsMetaData = (
  caseUnitLabel: string | null,
  otherUnitLabel: string | null,
  packUnitLabel: string | null,
  isNonEligible: boolean,
  batchIdConfiguration:boolean = false,
  enableFocPurchaseReturnClaims: boolean = false
) => {
  const createPTDColumn = (label: string, value: string) => ({
    headerName: `PTD (${label})`,
    mappedValue: value,
    width: 120,
    headerAlign: "left",
    align: "left",
    includeCurrenySymbol: true,
  });

  const ptdColumns = !batchIdConfiguration
    ? [
        caseUnitLabel && createPTDColumn(caseUnitLabel, "ptdCs"),
        otherUnitLabel && createPTDColumn(otherUnitLabel, "ptdOther"),
        packUnitLabel && createPTDColumn(packUnitLabel, "ptdPc"),
      ].filter(Boolean)
    : [{
        headerName: "Batch IDs",
        mappedValue: "batchIdsCount",
        width: 120,
        headerAlign: "left",
        align: "left",
      }];

  const columns = [
    {
      headerName: "SKU Code",
      mappedValue: "skuCode",
      width: 180,
      headerAlign: "left",
      align: "left",
      hasInfoTooltip: isNonEligible,
      infoTooltipMessage: "SKUs marked with red warning icon are non-eligible.",
      hasFocIcon: enableFocPurchaseReturnClaims
    },
    { headerName: "SKU Name", 
      mappedValue: "skuName", 
      width: 280, 
      headerAlign: "left", 
      align: "left" },
    { headerName: "Damage Qty", 
      mappedValue: "totalDamagedCs,totalDamagedPc,totalDamagedOther", 
      width: 180, 
      headerAlign: "left", 
      align: "left", 
      type: "order" },
    ...ptdColumns,
    {
      headerName: "Total Claim Amount",
      headerParts: { top: "Total Claim", bottom: "Amount" },
      mappedValue: "totalClaimAmount",
      width: 200,
      headerAlign: "left",
      align: "left",
      includeCurrenySymbol: true,
    },
    { headerName: "", 
      mappedValue: "removePurchaseReturnsClaims", 
      width: 30, 
      headerAlign: "left", 
      align: "center" 
    },
  ];

  return {
    columns,
    Actions: [],
  };
};
